import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from "@angular/core";
import { CardModel } from "../../../swagger/api.client.generated";
import {
  CardActionEvent,
  CardSize,
  CardVariant,
  ControlOptions,
  UserAttribute,
} from "./card.models";
import { resolveCardVariant } from "./card-type.utils";

// Thin dispatcher: receives raw CardModel, resolves a CardVariant, and delegates rendering
// to the appropriate typed sub-component (SafetyCard or ActivityCard). All visual logic
// lives in those sub-components — this file should never contain CSS class or colour logic.
@Component({
  selector: "app-cards",
  templateUrl: "./card.component.html",
  styleUrls: ["./card.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardComponent {
  // Setter (not plain @Input) so the variant is re-resolved whenever the card changes.
  @Input() public set card(value: CardModel) {
    this.cardModel = value;
    this.cardVariant = resolveCardVariant(this.cardModel, this.isPlaceholderEmpty);
  }
  @Input() public set controlsOptions(value: ControlOptions) {
    this.controls = value;
  }
  @Input() public editMode: boolean = false;
  @Input() public set selected(value: boolean) {
    this.cardSelected = value;
  }
  // Setter re-resolves variant because isEmpty can arrive before `card` is set.
  @Input() public set isEmpty(value: boolean) {
    this.isPlaceholderEmpty = value;
    this.cardVariant = resolveCardVariant(this.cardModel, this.isPlaceholderEmpty);
  }
  @Input() public dragDisabled: boolean = false;
  /** ms before a press-and-hold becomes a drag; forwarded to the typed card + shell. Default 250. */
  @Input() public dragStartDelay: number | { touch: number; mouse: number } = 250;
  @Input() public placeholderKind: "activity" | "safety" = "activity";

  // No-op: fixed desktop size replaces the zoom system. CardSize kept in card.models.ts
  // because CardLocalService and carousel/plan consumers still reference the type.
  @Input() public set cardSize(_value: CardSize) {}

  @Output() public cardAction = new EventEmitter<CardActionEvent>();
  @Output() public userAttributes = new EventEmitter<UserAttribute>();

  cardModel: CardModel | null = null;
  controls: ControlOptions = {
    isFavorite: false,
    isMyCard: false,
    showControl: false,
    showHamburgerMenu: false,
    showCheckIcon: false,
  };
  cardSelected: boolean = false;
  isPlaceholderEmpty: boolean = false;

  cardVariant: CardVariant | undefined;

  get activityVariant() {
    return this.cardVariant?.kind === "activity" ? this.cardVariant : null;
  }

  get safetyVariant() {
    return this.cardVariant?.kind === "safety" ? this.cardVariant : null;
  }
}
